import { action } from "mobx";
import { DynamicMapStoreBase } from "../DynamicMapStoreBase";
import { ProfileKeyCode, DynamicProfileLayerType, SketchState } from "../DynamicProfileEnums";
import { DynamicCrossSectionHub } from "./DynamicCrossSectionHub";
import Extent from "@arcgis/core/geometry/Extent";
import Polyline from "@arcgis/core/geometry/Polyline";
import { Geometry, Graphic, GraphicsLayer, Point, SimpleLineSymbol, esriGeometryEngine, esriProjection } from "cd-eft/utils/esriTypes";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { XyzPoint } from "cd-eft/models/SurveyEngineering/XyzPoint";
import { getElevationUsingContours } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import stores from "../..";
import { doEvents } from "cd-eft/utils/sysUtils";
import { getBoundaryPolygon, getStructureExteriorPolygon, isPointInsidePolygon } from "cd-eft/utils/gisUtils";
import {StructureProfileController} from 'cd-eft/services/eftSetApi';
import { ElevationInterpolater } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';

const { designStore, projectsLibraryStore, waterwayDesignStore, terraceDesignStore } = stores.AppStore;

const crossSectionSymbol = {
    designLineSymbol: new SimpleLineSymbol({ color: [0, 255, 60], width: 3 }),
    invalidLineSymbol: new SimpleLineSymbol({ color: 'red', width: 5 }),
};

const unitFactor = 3.28084;   // if mapView projection or data projection change, the factor needs to change too.
// TODO: to clean up.
const ERROR_SELF_CROSSING = 'Common_29';
const ERROR_POINT_OUTSIDE = 'CS_4';

class DynamicCrossSectionMapStore extends DynamicMapStoreBase {
    protected hub: DynamicCrossSectionHub;
    private _isValidLine = true;
    private _loopThreshold = 0;
    private userDataXyzPoints: any[] = [];
    /**
     *
     */
    constructor(hub: DynamicCrossSectionHub) {
        super();

        this.hub = hub;
        this.profileKeys = [ProfileKeyCode.CSD, ProfileKeyCode.CSO,ProfileKeyCode.WTL];

    }
    /** supporting functions */
    private _checkMapViewExtent(x: number, y: number, radius = 5000): void {
        if (this.mapView) {
            if (radius * 1.9 < Math.min(this.mapView.extent.height, this.mapView.extent.width)) {
                const newExtent = new Extent({
                    xmin: x - 0.5 * radius,
                    xmax: x + 1.5 * radius,
                    ymin: y - radius,
                    ymax: y + radius,
                    spatialReference: this.mapSpatialReference,
                });
                this.mapView.extent = newExtent;
            }
        }
    }

    private async isValidLastSegment(line: Polyline) {
        let isValid = true;

        const path = line.paths[0];
        const length = path.length;

        // check loops
        if (path.length >= 2) {
            const checkerPoint = line.getPoint(0, length - 1);
            for (let index = 0; index < length - 2; index++) {
                const checkeePoint = line.getPoint(0, index);
                if (esriGeometryEngine.distance(checkerPoint, checkeePoint) < this._loopThreshold) {
                    isValid = false;
                    this._isValidLine = false;
                    MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
                    break;
                }
            }
        }

        // check self-crossing
        if (isValid && path.length >= 3) {
            const spatialReference = line.spatialReference;
            const lastSegment = new Polyline({ paths: [[path[length - 2], path[length - 1]]], spatialReference });

            for (let index = 0; index < length - 3; index++) {
                const pntArr1 = path[index];
                const pntArr2 = path[index + 1];
                const currentSegment = new Polyline({ paths: [[pntArr1, pntArr2]], spatialReference });
                if (esriGeometryEngine.crosses(currentSegment, lastSegment)) {
                    isValid = false;
                    this._isValidLine = false;
                    MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
                    break;
                }
            }
        }

        // check outside boundary
        const index = path.length - 1;
        const mapPoint = new Point({ x: path[index][0], y: path[index][1], spatialReference: this.mapSpatialReference });
        const dataPoint = esriProjection.project(mapPoint as Geometry, this.dataSpatialReference!);
        const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
        const pointInsidePolygon = await isPointInsidePolygon((dataPoint as Point).x, (dataPoint as Point).y, bPolygon, projectsLibraryStore.projectSpatialReference!);
        if (!pointInsidePolygon) {
            isValid = false;
            this._isValidLine = false;
            MessageCenter.addErrorFromCode(ERROR_POINT_OUTSIDE);
        }

        return isValid;
    }

    private clearInvalidState(graphic: Graphic | undefined, delay = 0) {
        this._isValidLine = true;
        if (graphic) {graphic.symbol = crossSectionSymbol.designLineSymbol};

        setTimeout(() => {
            MessageCenter.clearMessages();
        }, delay);
    }

    @action
    protected reset(): void {
        this.sketchLayer.graphics.removeAll();
        this.curveLayer.graphics.removeAll();
        designStore.crossSectionDrawingComplete = false;
    }

    // public implementations of abstract methods
    moreSketchVmSetup() {
        // throw new Error("Method not implemented.");
    }

    SketchPolyline() {
        this.reset();
        this.broadcastSketchState({ state: SketchState.START, keyCode: this.profileKeys[0] });
        if (this.profileKeys[1]) this.broadcastSketchState({ state: SketchState.START, keyCode: this.profileKeys[1] });
        this.mainSketchVM?.create('polyline');
        this._mapTooltipProvider?.start();
    }

    toggleAlignmentSnapping() {
        // throw new Error("Method not implemented.");
    }

    LoadEditToMap(data: any, keyCode: ProfileKeyCode) {
        // throw new Error("Method not implemented.");
    }
    drawCurve(curveData: any) {
        // throw new Error("Method not implemented.");
    }
    removeMapLayer(layer: DynamicProfileLayerType) {
        super.reomvoePrimary();
    }
    configSketchVM() {
        // throw new Error("Method not implemented.");
    }
    checkToConvertAlignment(index) { }

    redrawAlignmentProfile() {
        // Only used for alignment
    }

    SketchPoint() {
        // not used for cross section. If needed later, see code in DynamicAlignmentMapStore.
    }

    ReshapePolyline() {
        // not used for cross section. If needed later, see code in DynamicAlignmentMapStore.
    }

    updateAlignmentOnMap(data: any, keyCode: ProfileKeyCode) { }

    handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
        // not used for cross section.
        throw new Error("Method not implemented.");
    }
    handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void> {
        // not used for cross section.
        throw new Error("Method not implemented.");
    }
    generateAlignmentLabelsLayer(){}
    // end of public implementations of abstract methods
    // ******************************************************

    async handlePolylineCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
        if (event.state === 'start' || event.state === 'active') {
            if (event.toolEventInfo.type === 'vertex-add') {
                const [x, y] = event.toolEventInfo.vertices[0].coordinates;
                if (event.state === 'start') {
                    this._checkMapViewExtent(x, y);
                }

                if (this._isValidLine) {
                    const line = event.graphic.geometry as Polyline;
                    if (await this.isValidLastSegment(line)) {
                        this.clearInvalidState(event.graphic);

                        const mapPoint = new Point({ x: x, y: y, spatialReference: this.mapSpatialReference });
                        const dataPoint = esriProjection.project(mapPoint as Geometry, this.dataSpatialReference!);

                        // prepare for profile view
                        const dataX = (dataPoint as Point).x;
                        const dataY = (dataPoint as Point).y;
                        let dataZ = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, mapPoint) : await getElevationUsingContours(dataPoint, mapPoint, this.mapView);

                        if (dataZ === undefined) {
                            dataZ = 0;
                        }

                        const newVertex = new XyzPoint(dataX, dataY, dataZ);
                        if (this._mapTooltipProvider) this._mapTooltipProvider.currentVertex = newVertex;
                        this.userDataXyzPoints.push(newVertex);
                    } else {
                        event.graphic.symbol = crossSectionSymbol.invalidLineSymbol;
                    }
                } else {
                    // This is for the case of "slow" twice clicks
                    this.mainSketchVM?.complete();
                    const length = (event.graphic.geometry as Polyline).paths[0].length;
                    super.deleteVertex({ index: length - 1 });
                    event.graphic.symbol = crossSectionSymbol.designLineSymbol;
                    this._isValidLine = true;
                }
            } else if (event.toolEventInfo.type === 'cursor-update') {
                if (!this._isValidLine) {
                    this.mainSketchVM?.undo();
                    this.clearInvalidState(event.graphic, 1500);
                }
            }
        }

        if (event.state === 'complete') {
            this._mapTooltipProvider?.stop();

            if (!this._isValidLine) {
                const length = (event.graphic.geometry as Polyline).paths[0].length;
                super.deleteVertex({ index: length - 1 });
                this.clearInvalidState(void 0, 1500);
            }

                try {
                    this.startLengthyProfileProcess(true);
                    await doEvents();

                    const crossSectionLine: {}[] = [];
                    const userMapPoints = (event.graphic.geometry as Polyline).paths[0];
                    for (const point of userMapPoints) {
                        const mapPoint = new Point({x: point[0], y: point[1], spatialReference: this.mapView!.spatialReference});
                        const dataPoint = esriProjection.project(mapPoint as Geometry, this.dataSpatialReference!) as Point;
                        const obj = {
                            cutOrFill: 0,
                            description: "string",
                            id: 0,
                            keyString: "string",
                            objectName: "string",
                            pointName: "string",
                            tinnable: true,
                            visiable: true,
                            x: dataPoint.x,
                            y: dataPoint.y,
                            z: 0
                        }
                        crossSectionLine.push(obj);
                    }

                    let id: number = 0;
                    let json: string | undefined = undefined;
                    let designElementTypeId: number | undefined = undefined;
                    // If on ADD NEW STRUCTURE page. NOTE: this currentDesignElementJson only exists after user clicks "Design" on a structure, but before they click "Save"
                    if (designStore.currentDesignElementJson && designStore.currentDesignElementJson.type) {
                        json = JSON.stringify(designStore.currentDesignElementJson);
                        switch(designStore.currentDesignElementJson.type) {
                            case "Rectangular Pit":
                                designElementTypeId = 11;
                                break;
                            case "Round Pit":
                                designElementTypeId = 12;
                                break;
                            case "Diked Pit":
                                designElementTypeId = 13;
                                break;
                            case "Pad":
                                designElementTypeId = 14;
                                break;
                            case "Land Leveling":
                                designElementTypeId = 15;
                                break;
                        }
                    } else {
                        id = await this.getIntersectingStructureId();
                    }

                    designStore.singleCrossSectionDesignElementId = id;
                    
                    // Handle waterways
                    if (waterwayDesignStore.currentWaterwayDesignElementTypeId === 7 || waterwayDesignStore.currentWaterwayDesignElementTypeId === 19) {
                        designElementTypeId = waterwayDesignStore.currentWaterwayDesignElementTypeId 
                        ? waterwayDesignStore.currentWaterwayDesignElementTypeId 
                        : (waterwayDesignStore.waterwayType === "Lined Waterway" ? 19 : 7)
                        json = undefined;
                    } else if (waterwayDesignStore.currentWaterwayDesignElementTypeId === 0) {
                        const waterway = waterwayDesignStore.summaryWaterwayTypes.find(item => item.elementId === id);
                        if (waterway) {
                            designElementTypeId = waterway.type === "Lined" ? 19 : 7;
                            json = undefined;
                        }
                    }

                    // Handle terraces 
                    if (terraceDesignStore.terraceType !== 0) {
                        designElementTypeId = terraceDesignStore.terraceType;
                        json = undefined;
                    } else {
                        const terrace = terraceDesignStore.summaryTerraceTypes.find(item => item.elementId === id);
                        if (terrace) {
                            designElementTypeId = terrace.type;
                            json = undefined;
                        }
                    }


                    const payload = {
                        crossSectionLine: crossSectionLine,
                        sampleInterval: 3,
                        sessionUuid: designStore.projectsStore.currentSessionId,
                        structureElementId: id,
                        designElementTypeId,
                        json
                    }
                    const response = await StructureProfileController.generateCrossSectionUsingPOST(payload);
                    
                    if (response.data) {
                        const profileStore = this.hub.crossSectionProfileStore;
                        const tableStore = this.hub.crossSectionTableStore;

                        await profileStore?.drawSingleCrossSectionProfiles(response);
                        await tableStore?.prepareTablePoints(response);
                    }
                } catch (error) {
                    if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
                        MessageCenter.addError((error as any).response.data.errorMessage);
                    } else {
                        MessageCenter.addError((error as any).message);
                    }
                } finally {
                    this.startLengthyProfileProcess(false);
                    this.broadcastSketchState({ state: SketchState.COMPLETE, keyCode: this.profileKeys[0] });
                }
            designStore.crossSectionDrawingComplete = true;
            this.userDataXyzPoints = [];
        }

        if (event.state === 'cancel') {
            this._mapTooltipProvider?.stop();
            this.originalProfilePoints = [];
            this.activeProfilePoints = [];
            this.userDataXyzPoints = [];
            this.broadcastSketchState({ state: SketchState.CANCEL, keyCode: this.profileKeys[0] });
        }
    }

    private async getIntersectingStructureId() {
        let structureId: number = 0;
        const userPoints = this.userDataXyzPoints;
        const borderLayers = this.mapView?.map.allLayers.filter((layer) => {
            return layer.id.includes("intersect_border");
        });
        if (borderLayers) {
            for (let i = 0; i < userPoints.length - 1; i++) {
                const data = this.prepareIntersectsData(userPoints, i);
                for (const layer of borderLayers) {
                    const lyr = layer as GraphicsLayer;
                    const graphics = lyr.graphics;
                    
                    for (const graphic of graphics) {
                        const intersects = esriGeometryEngine.intersects(graphic.geometry, data.line);
                        if (intersects) {
                            const id = lyr.id.substring(lyr.id.indexOf('-') + 1);
                            structureId = Number(id);
                            break;
                        }
                    }
                }
            }
        }
        if (structureId === 0) {
            // check if cross section is inside structure
            const exteriorBorders = this.mapView?.map.allLayers.filter((layer) => {
                return layer.title?.includes("Exterior Border");
            });
            if (exteriorBorders && exteriorBorders.length > 0) {
                for (const border of exteriorBorders) {
                    const lyr = border as GraphicsLayer;
                    const graphics = lyr.graphics as any;
                    const geom = graphics.items[0].geometry as Polyline;
                    const structurePolygon = getStructureExteriorPolygon(geom, this.mapSpatialReference);
                    
                    for (const point of userPoints) {
                        const dataPoint = new Point({x: point.x, y: point.y, spatialReference: this.dataSpatialReference!});
                        const mapPoint = esriProjection.project(dataPoint, this.mapSpatialReference!) as Point;
                        const pointInsidePolygon = await isPointInsidePolygon(mapPoint.x, mapPoint.y, structurePolygon, this.mapSpatialReference!);
                        if (pointInsidePolygon) {
                            const id = lyr.id.substring(lyr.id.indexOf('-') + 1);
                            structureId = Number(id);
                            break;
                        }
                    }
                }
            }
        }
        return structureId;
    }

    // dummy implementation  of abstract methods
    setAlignment(data: any) { }
    clearAlignment() { }
    setTracker(d: number, y: number) { }
    clearTracker() { }
    addStations() { }
    clearReachStations() { }

    // private async getStructureIntersects() {
    //     const tablePoints: any[] = [];
    //     const userPoints = this.userDataXyzPoints;
    //     const borderLayers = this.mapView?.map.allLayers.filter((layer) => {
    //         return layer.id.includes("intersect_border");
    //     });

    //     if (borderLayers) {
    //         // process structure borders for each vertex pair
    //         for (let i = 0; i < userPoints.length - 1; i++) {
    //             const data = this.prepareIntersectsData(userPoints, i);

    //             tablePoints.push(data.projectedStartPoint);

    //             const intersections: any[] = [];
    //             borderLayers?.forEach((layer) => {
    //                 const lyr = layer as GraphicsLayer;
    //                 const graphics = lyr.graphics;

    //                 // grab all borders that intersect vertex pair
    //                 const intersectingBorderGeoms: any[] = [];
    //                 graphics.forEach((graphic) => {
    //                     const intersects = esriGeometryEngine.intersects(graphic.geometry, data.line);
    //                     if (intersects) intersectingBorderGeoms.push(graphic.geometry);
    //                 });

    //                 // get coordinates of each intersection
    //                 intersectingBorderGeoms.forEach((geom) => {
    //                     const cut = esriGeometryEngine.cut(data.line, geom) as any;
    //                     if (cut) {
    //                         for (const path of cut[0].paths) {
    //                             for (const point of path) {
    //                                 // omit intersections that match start or end points
    //                                 if ((point[0] !== data.projectedStartPoint.x) && (point[1] !== data.projectedStartPoint.x) && (point[0] !== data.projectedEndPoint.x) && (point[1] !== data.projectedEndPoint.x)) {
    //                                     const newXyzPoint = new XyzPoint(point[0], point[1], 0);
    //                                     const distance = getDistance2D(newXyzPoint, data.startPoint);

    //                                     let z = 0;
    //                                     let pnt = new Point({ x: point[0], y: point[1], spatialReference: this.mapView!.spatialReference });
    //                                     let found = false;
    //                                     for (const path of (geom as Polyline).paths) {
    //                                         let begin = path[0];
    //                                         for (let index = 1; index < path.length; index++) {
    //                                             const end = path[index];
    //                                             const segment = new Polyline({ paths: [[begin, end]], spatialReference: this.mapView!.spatialReference })
    //                                             if (esriGeometryEngine.intersects(segment, pnt)) {

    //                                                 const lower = begin[2] <= end[2] ? begin : end;
    //                                                 const deltaX = Math.abs(lower[0] - pnt.x);
    //                                                 const deltaY = Math.abs(lower[1] - pnt.y);
    //                                                 const wholeX = Math.abs(begin[0] - end[0]);
    //                                                 const wholeY = Math.abs(begin[1] - end[1]);
    //                                                 z = Math.sqrt(deltaX * deltaX + deltaY * deltaY) / Math.sqrt(wholeX * wholeX + wholeY * wholeY) * Math.abs(begin[2] - end[2]) + lower[2];
    //                                                 z = z * unitFactor;
    //                                                 console.log({ begin, end, pnt, z });
    //                                                 found = true;
    //                                                 pnt = new Point({ x: point[0], y: point[1], z, spatialReference: this.mapView!.spatialReference });
    //                                                 break;
    //                                             }
    //                                             begin = end;
    //                                         }
    //                                         if (found) break;
    //                                     }
    //                                     const intersection = {
    //                                         point: pnt,
    //                                         distance
    //                                     }
    //                                     intersections.push(intersection);
    //                                 }
    //                             }
    //                         }
    //                     }
    //                 });
    //             });
    //             // order intersections by distance from start point
    //             intersections.sort((a, b) => {
    //                 return a.distance - b.distance;
    //             });
    //             intersections.forEach((intersection) => {
    //                 tablePoints.push(intersection.point);
    //             });
    //             if (i === (userPoints.length - 2)) tablePoints.push(data.projectedEndPoint);
    //         }
    //     }
    //     this.tableMapPoints = Array.from(new Set(tablePoints));
    // }
}

export { DynamicCrossSectionMapStore }